// Features: ML001-F0005, ML001-F0006, ML001-F0097, M004-F0304, M001-F0215, ML001-F0117, ML001-F0119, M004-F0707, ML001-F0218 (Credit notes tag, WebUI #169)
// ML001-F0239: money controls only for a viewer holding ledger_manage on ANOTHER member's account (T-LED-SOD-004, T-LED-SOD-005).
// Tests: T-LED-SOD-004, T-LED-SOD-005, T-LED-WEB-001, T-LED-WEB-002, T-LED-WEB-003, T-LED-WEB-004, T-LED-WEB-005, T-LED-CRED-001, T-LED-CRED-004, T-LED-LEDG-007, T-LED-LEDG-013, T-LED-NODEUI-011, T-LED-NODEUI-013, T-M004-0192, T-M004-0193, T-LED-CWEB-003

import TxFinder from './TxFinder';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from './layout/Layout';
import LedgerQueueSections from './LedgerQueueSections';
import TxChip from './TxChip';
import { useAuth } from '../context/AuthContext';
import { ledgerAccountApi, organisationApi } from '../services/api';
import CreditNotesPanel from './ledger/CreditNotesPanel';
import { canViewCreditNotes } from './ledger/creditNoteModel';
import MemberInvoicesPanel from './ledger/MemberInvoicesPanel';
import { canViewInvoices } from './invoices/invoiceModel';
import { canPerformMoneyActions } from './ledger/moneyActionGate';
import BillingProfileDrawer from './ledger/billingProfile/BillingProfileDrawer';
import './LedgerAccountPage.css';

const payload = (response) => response?.data?.data || response?.data || response || {};
const list = (value, keys) => keys.reduce((found, key) => found || (Array.isArray(value?.[key]) ? value[key] : null), null) || (Array.isArray(value) ? value : []);
const memberId = (member) => Number(member?.id || member?.user_id || 0);
const memberName = (member) => member?.display_name || member?.displayName || member?.name || member?.primary_email || member?.email || `Member ${memberId(member)}`;
const money = (value, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value || 0));
const apiError = (error) => error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'The member Ledger could not be loaded.';
// ML001-F0097 / T-LED-WEB-005: unwrap the manager-scoped member entries cursor
// page ({ success, attachment, data: [...], meta }) into rows + pagination meta.
const entriesPage = (response) => {
  const body = response?.data && typeof response.data === 'object' && !Array.isArray(response.data) ? response.data : {};
  if (Array.isArray(body.data)) return { rows: body.data, meta: body.meta && typeof body.meta === 'object' ? body.meta : null };
  if (Array.isArray(body)) return { rows: body, meta: null };
  return { rows: [], meta: null };
};
const entryDate = (entry) => {
  const raw = entry?.created_at || entry?.transaction_date;
  return raw ? new Date(raw).toLocaleDateString() : '—';
};

export function selectOrganisationMember(response, userId) {
  return list(payload(response), ['users', 'members', 'items']).find((member) => String(memberId(member)) === String(userId)) || null;
}

// M004-F0712 S8 / owner O2: the Ledger's own entry number (TXN-...) is labelled "Ledger entry" and kept; the TX chip (from the
// entry's hand_off_id, when the Ledger sends one) is its own "TX" column, so the two numbers never share the word "transaction".
// `orgId`/`customerUserId` let the chip open the history; without them (or on an own-account page) it is copy-only.
export function EntriesTable({ rows, orgId, customerUserId, copyOnly = false }) {
  return (
    <table>
      <thead><tr><th>Ledger entry</th><th>TX</th><th>Description</th><th>Amount</th><th>Date</th></tr></thead>
      <tbody>
        {rows.map((entry) => (
          <tr key={entry.transaction_id}>
            <td>{entry.transaction_id}</td>
            <td><TxChip handOffId={entry.hand_off_id} orgId={orgId} customerUserId={customerUserId} copyOnly={copyOnly} /></td>
            <td>{entry.description}</td>
            <td>{money(entry.amount, entry.currency)}</td>
            <td>{entryDate(entry)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function LedgerAccountPage({ own = false }) {
  const { orgId } = useParams();
  const { user } = useAuth();
  const [organisation, setOrganisation] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedId, setSelectedId] = useState(own ? String(user?.id || '') : '');
  const [account, setAccount] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [entriesMeta, setEntriesMeta] = useState(null);
  const [entriesError, setEntriesError] = useState('');
  const [entriesLoading, setEntriesLoading] = useState(false);
  const entriesRequestId = useRef(0);
  // WebUI #169 / ML001-F0218: the Credit notes tag. Permissions decide whether
  // the tag exists at all (fail closed: an unreadable projection hides it).
  const [permissions, setPermissions] = useState(null);
  const [tag, setTag] = useState('account');
  // M004-F0795 (WebUI #263): the customer billing profile is a manager surface. Shown only for ledger_manage on a member page (the own page never gets it);
  // a ledger_read caller gets no tag, and the drawer's read is never called for them. The viewer's own account is shown too: Save is refused by the Ledger.
  const [billingOpen, setBillingOpen] = useState(false);
  const canManageBilling = !own && permissions?.ledger_manage === true;

  useEffect(() => {
    if (own) return undefined;
    let current = true;
    organisationApi.getMyPermissions(orgId)
      .then((response) => { if (current) setPermissions(payload(response)?.permissions || {}); })
      .catch(() => { if (current) setPermissions({}); });
    return () => { current = false; };
  }, [orgId, own]);

  useEffect(() => {
    let current = true;
    Promise.all([organisationApi.getMine(), own ? Promise.resolve(null) : ledgerAccountApi.getMembers(orgId)])
      .then(([organisationsResponse, membersResponse]) => {
        if (!current) return;
        const organisations = list(payload(organisationsResponse), ['organisations', 'items']);
        setOrganisation(organisations.find((item) => String(item.id || item.organisation_id) === String(orgId)) || null);
        if (!own) setMembers(list(payload(membersResponse), ['users', 'members', 'items']));
        if (own) setSelectedId(String(user?.id || ''));
        setLoading(false);
      })
      .catch((cause) => { if (current) { setError(apiError(cause)); setLoading(false); } });
    return () => { current = false; };
  }, [orgId, own, user?.id]);

  const loadAccount = useCallback(async (targetId = selectedId) => {
    if (!targetId) { setAccount(null); return; }
    setLoading(true); setError('');
    try { setAccount(payload(await ledgerAccountApi.getAccount(orgId, targetId))); }
    catch (cause) { setAccount(null); setError(apiError(cause)); }
    finally { setLoading(false); }
  }, [orgId, selectedId]);

  useEffect(() => { if (selectedId) loadAccount(selectedId); }, [selectedId, loadAccount]);

  // ML001-F0097 / T-LED-WEB-005: the member view fetches the itemised entries
  // from the manager-scoped paginated endpoint; the summary read above stays
  // responsible for balance/credits/attachment figures only.
  const loadEntries = useCallback(async (targetId = selectedId, cursor) => {
    if (!targetId) { setEntries([]); setEntriesMeta(null); setEntriesError(''); return; }
    const requestId = ++entriesRequestId.current;
    setEntriesLoading(true);
    setEntriesError('');
    try {
      const page = entriesPage(await ledgerAccountApi.getMemberEntries(orgId, targetId, cursor ? { cursor, limit: 50 } : { limit: 50 }));
      if (entriesRequestId.current !== requestId) return;
      setEntries((current) => (cursor ? [...current, ...page.rows] : page.rows));
      setEntriesMeta(page.meta);
    } catch (cause) {
      if (entriesRequestId.current !== requestId) return;
      setEntriesError(apiError(cause));
      if (!cursor) { setEntries([]); setEntriesMeta(null); }
    } finally {
      if (entriesRequestId.current === requestId) setEntriesLoading(false);
    }
  }, [orgId, selectedId]);

  useEffect(() => {
    if (own) return;
    if (selectedId) loadEntries(selectedId);
    else { setEntries([]); setEntriesMeta(null); setEntriesError(''); }
  }, [selectedId, loadEntries, own]);

  const visibleMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? members.filter((member) => `${memberName(member)} ${member.primary_email || ''}`.toLowerCase().includes(term)) : members;
  }, [members, search]);
  // ML001-F0239 (Core ML001-D007 7.8.5): `own` pages never act; a manager page acts only when ledger_manage is held AND the
  // selected account is not the viewer's own. Fail closed while permissions are unknown.
  const canAct = !own && canPerformMoneyActions({ permissions, viewerId: user?.id, targetUserId: selectedId });
  const selectedMember = own ? user : members.find((member) => String(memberId(member)) === String(selectedId));

  return <Layout><div className="ledger-account-page">
    <header className="ledger-account-heading"><Link to="/app/ledger">← Ledger home</Link><span>{organisation?.name || organisation?.organisation_name || 'Organisation Ledger'}</span><h1>{own ? 'My account' : 'Member accounts'}</h1><p>{own ? 'Your balance, credits and transactions in this organisation.' : 'Open a person’s organisation Ledger and manage their credit.'}</p></header>
    {/* M004-F0714: the Ledger manager's entry to the SAME history drawer. Own-account views never get it (P5). */}
    {!own ? <TxFinder orgId={orgId} /> : null}
    {error ? <p className="ledger-account-error" role="alert">{error}</p> : null}
    <div className={`ledger-account-layout ${own ? 'ledger-account-own' : ''}`}>
      {!own ? <aside className="ledger-member-picker" aria-label="Organisation members"><label>Find a member<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or email" /></label><div className="ledger-member-list">{visibleMembers.map((member) => <button type="button" className={String(memberId(member)) === String(selectedId) ? 'active' : ''} key={memberId(member)} onClick={() => setSelectedId(String(memberId(member)))}><strong>{memberName(member)}</strong><small>{member.primary_email || member.org_role || 'Organisation member'}</small></button>)}</div>{!loading && !visibleMembers.length ? <p>No matching members.</p> : null}</aside> : null}
      <section className="ledger-account-detail">
        {!selectedId && !loading ? <div className="ledger-account-empty"><h2>Choose a member</h2><p>Select a person to see the Ledger attached to this organisation.</p></div> : null}
        {selectedId && !own && (canViewCreditNotes(permissions) || canViewInvoices(permissions)) ? (
          <div className="ledger-account-tags" role="tablist" aria-label="Member Ledger views">
            <button type="button" role="tab" aria-selected={tag === 'account'} className={tag === 'account' ? 'active' : ''} data-testid="member-tab-account" onClick={() => setTag('account')}>Account</button>
            {canViewCreditNotes(permissions) ? <button type="button" role="tab" aria-selected={tag === 'credit-notes'} className={tag === 'credit-notes' ? 'active' : ''} data-testid="member-tab-credit-notes" onClick={() => setTag('credit-notes')}>Credit notes</button> : null}
            {/* M004-F0752 (WebUI #195): this member's invoices only; the gate is M004-F0749's (billing_transactions_view OR ledger_manage). */}
            {canViewInvoices(permissions) ? <button type="button" role="tab" aria-selected={tag === 'invoices'} className={tag === 'invoices' ? 'active' : ''} data-testid="member-tab-invoices" onClick={() => setTag('invoices')}>Invoices</button> : null}
          </div>
        ) : null}
        {loading ? <p role="status">Loading member Ledger…</p> : null}
        {tag === 'credit-notes' && !own && canViewCreditNotes(permissions) && selectedId ? (
          <CreditNotesPanel orgId={orgId} userId={selectedId} customerName={memberName(selectedMember)} permissions={permissions} viewerId={user?.id} />
        ) : tag === 'invoices' && !own && canViewInvoices(permissions) && selectedId ? (
          <MemberInvoicesPanel orgId={orgId} userId={selectedId} customerName={memberName(selectedMember)} />
        ) : account ? <>
          <div className="ledger-account-title"><div><span>Member Ledger</span><h2>{memberName(selectedMember)}</h2></div><span className={`ledger-attachment-state state-${account.attachment?.state || 'unknown'}`}>{account.attachment?.state === 'active' ? 'Account active' : account.attachment?.state === 'grace' ? 'Account settling' : account.attachment?.state === 'readonly' ? 'Read only' : 'Account status unavailable'}</span>
            {canManageBilling && selectedId ? <button type="button" className="ledger-billing-profile-tag" data-testid="billing-profile-tag" aria-haspopup="dialog" aria-expanded={billingOpen} onClick={() => setBillingOpen(true)}>Billing profile</button> : null}
          </div>
          {billingOpen && canManageBilling && selectedId ? <BillingProfileDrawer orgId={orgId} userId={selectedId} customerName={memberName(selectedMember)} onClose={() => setBillingOpen(false)} /> : null}
          <div className="ledger-account-figures"><div><span>Balance</span><strong>{money(account.balance)}</strong></div><div><span>Invoices</span><strong>{(account.invoices || []).length}</strong></div></div>
          {/* ML001-F0119 / T-LED-NODEUI-013: Advice then Approval, directly above Recent transactions, manager view only. */}
          {!own ? <LedgerQueueSections key={selectedId} orgId={orgId} userId={selectedId} customerName={memberName(selectedMember)} organisationName={organisation?.name || organisation?.organisation_name || ''} money={money} actorName={user?.displayName || user?.display_name || user?.name || ''} canAct={canAct} canEditText={permissions?.ledger_manage === true} onIssued={async () => { await loadAccount(selectedId); await loadEntries(selectedId); }} /> : null}
          {!own ? (
            <section className="ledger-account-section" data-testid="member-entries-section">
              <h3>Recent transactions</h3>
              {entriesError ? <p className="ledger-account-error" role="alert">{entriesError}</p> : null}
              {entriesLoading ? <p role="status">Loading transactions…</p> : null}
              {!entriesError && entries.length ? <>
                <EntriesTable rows={entries} orgId={orgId} customerUserId={selectedId} copyOnly={own} />
                {entriesMeta?.has_more && entriesMeta?.next_cursor ? <button type="button" className="ledger-entries-more" onClick={() => loadEntries(selectedId, entriesMeta.next_cursor)}>Show older transactions</button> : null}
              </> : (!entriesError && !entriesLoading ? <p>No transactions have been posted.</p> : null)}
            </section>
          ) : (
            <section className="ledger-account-section"><h3>Recent transactions</h3>{account.entries?.length ? <EntriesTable rows={account.entries} orgId={orgId} customerUserId={selectedId} copyOnly={own} /> : <p>No transactions have been posted.</p>}</section>
          )}
        </> : null}
      </section>
    </div>
  </div></Layout>;
}
